import moment from 'moment';
import React, { Fragment } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { groupBy, isEmpty, uniqBy } from 'lodash';
import cn from 'classnames';
import { GlobalToken } from 'antd';
import { useMeasurement } from '@/hooks/useMeasurement';
import UTCOffsetText from '@/comp/InvestigationDashboard/TabPane/UTCOffsetText';
import Super from '@/comp/Super';
import { AlertLevels } from '@/constants/enums';

import { TooltipContainer } from './styled';
import SeverityIcon from '../common/icons/SeverityIcon';
import { EngineStatus, EngineStatusInfo } from './EngineStatus';

export const dateFormat = 'ddd, MMM D, h:mm A';

interface Alert {
  id: string;
  severity: string;
  title: string;
  detailedDescription: string;
  openingTime: string;
  affectedDevicesNames: string[];
}

interface Point {
  point: { id: string; alerts: Alert[] };
  x: string;
  y: string;
  series: {
    options: { name: string; deviceName: string };
    yAxis: { axisTitle: { textStr: string } };
  };
  color: string;
}

const getUnitMeasurement = (metric: Point) => {
  const tmpUnit = metric?.series?.yAxis?.axisTitle?.textStr;
  if (tmpUnit === 'm^3/min') return 'm3/min';
  if (tmpUnit === 'kW/(m^3/min)') return 'kW/(m3/min)';

  return tmpUnit;
};

interface MetricProps {
  firstDateMetric: Point;
  secondDateMetric: Point;
  shouldGroupMetrics: boolean;
  isLastInGroup: boolean;
  isValidCompareMode: boolean;
  isDynamicController: boolean;
}

const Metric = (props: MetricProps) => {
  const { i18n } = useTranslation();
  const {
    firstDateMetric,
    secondDateMetric,
    shouldGroupMetrics,
    isLastInGroup,
    isValidCompareMode,
    isDynamicController,
  } = props;
  const {
    point: { id },
    color,
    y: value,
    series: {
      options: { name, deviceName },
    },
  } = firstDateMetric;
  const unit = getUnitMeasurement(firstDateMetric);
  const shouldRenderDivider = shouldGroupMetrics && isLastInGroup;

  return (
    <tr {...(shouldRenderDivider && { className: 'border-bottom' })} key={id}>
      <td className={cn('metric-name', shouldRenderDivider && 'padding-bottom')}>
        {!isValidCompareMode && !isDynamicController && <div className="circle" style={{ borderColor: color }}></div>}
        {isDynamicController && <div className="rectangle" style={{ backgroundColor: color }} />}
        <span className="name" style={isDynamicController && { fontWeight: 'bold' }}>
          {shouldGroupMetrics ? deviceName : name}
        </span>
      </td>

      {!isDynamicController && (
        <td className={cn(shouldRenderDivider && 'padding-bottom', !isValidCompareMode && 'metric-value')}>
          <div className="display-flex">
            {isValidCompareMode ? <div className="circle" style={{ borderColor: color }}></div> : null}
            <span className={cn('value', isValidCompareMode && 'no-margin-left')}>{value}</span>
            <span className="unit">{unit ? <Trans i18nKey={unit}>{i18n.exists(unit) && <Super />}</Trans> : null}</span>
          </div>
        </td>
      )}
      {isValidCompareMode ? (
        <td className={cn(shouldRenderDivider && 'padding-bottom', 'center-align')}>
          <div className="display-flex">
            <div className="circle" style={{ borderColor: secondDateMetric?.color }}></div>
            <span className={cn('value', 'no-margin-left')}>{secondDateMetric?.y}</span>
            <span className="unit">{unit ? <Trans i18nKey={unit}>{i18n.exists(unit) && <Super />}</Trans> : null}</span>
          </div>
        </td>
      ) : null}
    </tr>
  );
};

interface MetricGroupProps {
  metricName: string;
  firstDateMetrics: Point[];
  secondDateMetrics: Point[];
  shouldGroupMetrics: boolean;
  isLastGroup: boolean;
  isStackMode: boolean;
  isValidCompareMode: boolean;
  isDynamicController: boolean;
}

const MetricGroup = (props: MetricGroupProps) => {
  const { tunit } = useMeasurement();
  const { t } = useTranslation();
  const {
    metricName,
    firstDateMetrics,
    secondDateMetrics,
    shouldGroupMetrics,
    isLastGroup,
    isStackMode,
    isValidCompareMode,
    isDynamicController,
  } = props;
  const firstPeriodTotalPower = firstDateMetrics?.reduce((acc, metric) => acc + +metric?.y, 0);
  const secondPeriodTotalPower = secondDateMetrics?.reduce((acc, metric) => acc + +metric?.y, 0);

  return (
    <Fragment key={metricName}>
      {shouldGroupMetrics ? (
        <tr>
          <td>
            <div className="group-title">{metricName}</div>
          </td>
          {isStackMode ? (
            <>
              <td>
                <div className="total-power-container">
                  <div className="total-power">
                    {t('total')} {firstPeriodTotalPower} {t(tunit('kW'))}
                  </div>
                </div>
              </td>
              {isValidCompareMode ? (
                <td>
                  <div className="total-power-container">
                    <div className="total-power">
                      {t('total')} {secondPeriodTotalPower} {t(tunit('kW'))}
                    </div>
                  </div>
                </td>
              ) : null}
            </>
          ) : null}
        </tr>
      ) : null}
      {firstDateMetrics?.map((metric, index) => (
        <Metric
          key={metric?.point?.id}
          {...{
            firstDateMetric: metric,
            secondDateMetric: secondDateMetrics?.[index],
            shouldGroupMetrics,
            isLastInGroup: !isLastGroup && index === firstDateMetrics?.length - 1,
            key: metric?.point?.id,
            isValidCompareMode,
            isDynamicController,
          }}
        />
      ))}
    </Fragment>
  );
};

interface MetricsProps {
  firstDateMetrics: Point[];
  secondDateMetrics: Point[];
  shouldGroupMetrics: boolean;
  isStackMode: boolean;
  isValidCompareMode: boolean;
  isDynamicController: boolean;
}

const Metrics = (props: MetricsProps) => {
  const {
    firstDateMetrics,
    secondDateMetrics,
    shouldGroupMetrics,
    isStackMode,
    isValidCompareMode,
    isDynamicController,
  } = props;
  const firstPeriodMetricsByName = groupBy(firstDateMetrics, 'series.name');
  const secondPeriodMetricsByName = groupBy(secondDateMetrics, 'series.name');
  const metricNames = Object.keys(firstPeriodMetricsByName);

  return (
    <>
      {metricNames.map((metricName, index) => (
        <MetricGroup
          key={metricName}
          {...{
            metricName,
            firstDateMetrics: firstPeriodMetricsByName?.[metricName] as Point[],
            secondDateMetrics: secondPeriodMetricsByName?.[metricName] as Point[],
            shouldGroupMetrics,
            isStackMode,
            isLastGroup: index === metricNames?.length - 1,
            key: metricName,
            isValidCompareMode,
            isDynamicController,
          }}
        />
      ))}
    </>
  );
};

const Alerts = ({ points }: { points: Point[] }) => {
  const { t } = useTranslation();
  const alerts = points?.reduce(
    (result: Alert[], current: Point) => [...result, ...(current?.point?.alerts?.length ? current.point.alerts : [])],
    [],
  );
  const uniqueAlerts = uniqBy(alerts, 'id');
  const top3Alerts = uniqueAlerts?.slice(0, 3);
  const restAlerts = uniqueAlerts?.slice(3);
  return top3Alerts?.length ? (
    <div className="container">
      {top3Alerts?.map((alert: Alert) => (
        <div key={alert.id} className="alert-container">
          <div className="alert-severity-icon">
            <SeverityIcon severity={alert.severity as AlertLevels} />
          </div>
          <div className="alert-text">
            <div className="alert-title">{alert.title}</div>
            <div className="alert-description">{alert.detailedDescription}</div>
            <div className="alert-opening-time">{alert.affectedDevicesNames?.join(', ')}</div>
            <div className="alert-opening-time">{moment(alert.openingTime).format('ddd, MMM D [at] H:mm A')}</div>
          </div>
        </div>
      ))}
      {!isEmpty(restAlerts) ? (
        <div className="rest-alerts">{t('restAlerts', { restAlertsCount: restAlerts?.length })}</div>
      ) : null}
    </div>
  ) : null;
};

interface DateProps {
  date: string;
  isValidCompareMode?: boolean;
  shouldGroupMetrics?: boolean;
  className?: string;
}

export const Date = ({ date, isValidCompareMode, shouldGroupMetrics, className }: DateProps) => {
  const formattedDate = moment(+date).format(dateFormat);
  return (
    <div
      className={cn(
        'date',
        isValidCompareMode && 'column-flex',
        isValidCompareMode && 'no-margin-left',
        shouldGroupMetrics && 'no-margin-bottom',
        className || null,
      )}
    >
      {formattedDate}
      <span className="utc-offset-text">
        <UTCOffsetText />
      </span>
    </div>
  );
};

interface TooltipProps {
  x?: string;
  points?: Point[];
  resolutionLabel?: string;
  shouldGroupMetrics?: boolean;
  isStackMode?: boolean;
  isValidCompareMode?: boolean;
  isDynamicController?: boolean;
  token: GlobalToken;
  engineStatusInfo?: EngineStatusInfo | null;
  engineStateOnly?: boolean;
  engineStateDate?: string;
}

const MetricsTooltip = (props: TooltipProps) => {
  const { t } = useTranslation();
  const {
    points,
    resolutionLabel,
    shouldGroupMetrics,
    isStackMode,
    isValidCompareMode,
    isDynamicController,
    token,
    engineStatusInfo,
    engineStateOnly,
    engineStateDate,
  } = props;

  if (engineStateOnly && engineStatusInfo) {
    return (
      <TooltipContainer theme={{ token }} className="engine-state-only">
        <Date date={engineStateDate || ''} />
        <EngineStatus engineStatusInfo={engineStatusInfo} title={t('engineStatus')} token={token} />
      </TooltipContainer>
    );
  }

  const metricsByPeriod = groupBy(points, 'x');
  const dates = Object.keys(metricsByPeriod);

  //if highcharts bug is not happening, show data in same tooltip
  const isValidCompareModeWithCorrectData = isValidCompareMode && dates?.length === 2;

  return (
    <TooltipContainer theme={{ token }}>
      <table className={`metrics ${isDynamicController && 'dynamic-controller-table'}`}>
        <tr>
          {isValidCompareModeWithCorrectData ? <td /> : null}
          <td colSpan={2} className={`average-label ${isDynamicController && 'dynamic-controller-label'}`}>
            {resolutionLabel}
          </td>
        </tr>
        <tbody className={isDynamicController && 'dynamic-controller-body'}>
          <tr>
            {isValidCompareModeWithCorrectData ? <td /> : null}
            <td {...(!isValidCompareModeWithCorrectData && { colSpan: 2 })}>
              <Date
                {...{ date: dates[0], isValidCompareMode: isValidCompareModeWithCorrectData, shouldGroupMetrics }}
              />
            </td>
            {isValidCompareModeWithCorrectData ? (
              <td>
                <Date
                  {...{ date: dates[1], isValidCompareMode: isValidCompareModeWithCorrectData, shouldGroupMetrics }}
                />
              </td>
            ) : null}
          </tr>
          <Metrics
            {...{
              firstDateMetrics: metricsByPeriod[dates[0]],
              secondDateMetrics: metricsByPeriod[dates[1]],
              shouldGroupMetrics,
              isStackMode,
              isValidCompareMode: isValidCompareModeWithCorrectData,
              isDynamicController,
            }}
          />
        </tbody>
      </table>
      <Alerts points={points} />
      {engineStatusInfo && <EngineStatus engineStatusInfo={engineStatusInfo} title={t('engineStatus')} token={token} />}
    </TooltipContainer>
  );
};

export default MetricsTooltip;
